import PolarisIcon from '@/components/_common/PolarisIcon';
import {
  MILLISECONDS_PER_PIXEL,
  PIXELS_PER_MINUTE,
  SEGMENT_CONTAINER_PADDING,
  TIMELINE_MAX_SCALE,
  TIMELINE_MIN_SCALE,
  TIMELINE_SIDES_PADDING
} from '@/components/Packaging/constants';
import { useScrollZoom } from '@/components/Packaging/LayeredTimeline/hooks/useScrollZoom.hook';
import PlayHead from '@/components/Packaging/LayeredTimeline/PlayHead';
import Ruler from '@/components/Packaging/LayeredTimeline/Ruler';
import { ZoomControl } from '@/components/Packaging/LayeredTimeline/ZoomControl';
import { playerSeek } from '@/components/Packaging/utils';
import { LoadingStatuses } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  selectIsPlayerOpened,
  selectPackagingIsEditingTimeline,
  selectPackagingLoadingStatus,
  selectPackagingTimelineFrameDuration,
  selectPackagingTimelineScale
} from '@/redux/selectors/packagingSelectors';
import {
  selectHasFutureHistoryEntries,
  selectHasPastHistoryEntries,
  selectTimelineDuration,
  selectTimelineFramesCount,
  selectTimelineOverlays,
  selectTimelineSegmentsCount
} from '@/redux/selectors/packagingTimelineSelectors';
import { setTimelineScale } from '@/redux/slices/packagingSlice';
import { addOverlayLayer } from '@/redux/slices/packagingTimelineSlice';
import { SegmentService as PlayerAPI } from '@accurate-player/accurate-player-segment';
import styled from '@emotion/styled';
import { Box, Button, Typography } from '@mui/material';
import { clamp } from 'lodash';
import React, { useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { ActionCreators } from 'redux-undo';
import OverlaysTray from './OverlaysTray';
import SegmentsTray from './SegmentsTray';

interface LayeredTimelineProps {
  playerApiRef: React.MutableRefObject<PlayerAPI | null>;
  isDraggingNewSegment: boolean;
  isDraggingNewOverlay: boolean;
  isEditingTemplate?: boolean;
}

const LayeredTimeline = ({ playerApiRef, isDraggingNewSegment, isDraggingNewOverlay, isEditingTemplate = false }: LayeredTimelineProps) => {
  const dispatch = useAppDispatch();
  const scale = useAppSelector(selectPackagingTimelineScale);
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const framesCount = useAppSelector(selectTimelineFramesCount);
  const packagingLoadingStatus = useAppSelector(selectPackagingLoadingStatus);
  const isEditingTimeline = useAppSelector(selectPackagingIsEditingTimeline);
  const timelineDuration = useAppSelector(selectTimelineDuration);
  const segmentsCount = useAppSelector(selectTimelineSegmentsCount);
  const overlays = useAppSelector(selectTimelineOverlays);
  const canUndo = useAppSelector(selectHasPastHistoryEntries);
  const canRedo = useAppSelector(selectHasFutureHistoryEntries);
  const isPlayerOpened = useAppSelector(selectIsPlayerOpened);
  const wasAutoScaled = useRef(false); // flag to avoid automatically scaling the timeline more than once. For example, in case if `segmentsCount` change.
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const {
    wrapperRef,
    listeners: { onMouseMove: useScrollMouseMoveHandler, onScroll: useScrollScrollHandler }
  } = useScrollZoom();

  // Automatically scale timeline based on available space
  useLayoutEffect(() => {
    if (!segmentsCount || packagingLoadingStatus !== LoadingStatuses.fulfilled || wasAutoScaled.current) {
      return;
    }

    const availableSpace = document.body.clientWidth - TIMELINE_SIDES_PADDING * 2 - SEGMENT_CONTAINER_PADDING * 2;
    const timelineWidth = (timelineDuration / 60_000) * PIXELS_PER_MINUTE;
    const newScale = Math.min(Math.max(availableSpace / timelineWidth, TIMELINE_MIN_SCALE), TIMELINE_MAX_SCALE);

    dispatch(setTimelineScale(newScale));

    wasAutoScaled.current = true;
  }, [segmentsCount, packagingLoadingStatus]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (isEditingTimeline || (!e.ctrlKey && !e.metaKey)) {
        return;
      }

      if (e.key === 'z' && e.shiftKey && canRedo) {
        dispatch(ActionCreators.redo());
        return;
      }

      if (e.key === 'z' && !e.shiftKey && canUndo) {
        dispatch(ActionCreators.undo());
      }
    };

    document.body.addEventListener('keydown', listener);

    return () => {
      document.body.removeEventListener('keydown', listener);
    };
  }, [isEditingTimeline, canUndo, canRedo]);

  const addLayer = () => dispatch(addOverlayLayer());

  const handleScroll: React.UIEventHandler = (e) => {
    useScrollScrollHandler(e);
    forceUpdate(); // Force update is required to pass updated `timelineScrollPosition` prop to the Ruler component.
  };

  const handleTimelineClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!playerApiRef.current || !wrapperRef.current) {
      return;
    }

    if (e.target instanceof Element && e.target.matches('[data-prevent-timeline-seek], [data-prevent-timeline-seek] *')) {
      /**
       * Do not seek the video if the element that user clicked on is inside the element with `data-prevent-timeline-seek`
       * attribute. This attribute was specifically added to elements inside the timeline for that purpose.
       */
      return;
    }

    const pointerPositionOverTimeline = e.clientX + wrapperRef.current.scrollLeft - TIMELINE_SIDES_PADDING - SEGMENT_CONTAINER_PADDING;
    const frame = Math.floor((pointerPositionOverTimeline * MILLISECONDS_PER_PIXEL) / scale / frameDuration);
    const frameClamped = clamp(frame, 0, framesCount);

    playerSeek({
      playerApi: playerApiRef.current,
      frameIndex: frameClamped,
      dispatch,
      updateCurrentFrameInStore: true
    });
  };

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseMove={useScrollMouseMoveHandler}
      onScroll={handleScroll}
      onClick={handleTimelineClick}
      altcolor={+isEditingTemplate}
    >
      <Ruler timelineScrollPosition={wrapperRef.current?.scrollLeft ?? 0} />
      <InnerWrapper>
        <Box sx={{ pb: 3 }}>
          {isPlayerOpened && <PlayHead playerApiRef={playerApiRef} />}
          <FrozenTrayTitle variant="h5">Overlay</FrozenTrayTitle>
          <LayersDiv>
            {overlays.map((_, index) => (
              <OverlaysTray key={index} index={index} isHighlighted={isDraggingNewOverlay} />
            ))}
          </LayersDiv>

          <AddLayerButton variant="text" onClick={addLayer} endIcon={<PolarisIcon icon="add" height="15" width="15" />} data-prevent-timeline-seek>
            Add Layer
          </AddLayerButton>
        </Box>

        <TraysDivider />

        <Box sx={{ pt: 2, pb: 3 }}>
          <FrozenTrayTitle variant="h5">Segments</FrozenTrayTitle>
          <SegmentsTray isHighlighted={isDraggingNewSegment} isEditingTemplate={isEditingTemplate} />
        </Box>
        <ZoomControl />
      </InnerWrapper>
      <ScrollDivider />
    </Wrapper>
  );
};

export default React.memo(LayeredTimeline);

const Wrapper = styled.div<{ altcolor?: number }>`
  overflow-x: auto;
  user-select: none;

  margin-top: ${({ theme }) => theme.spacing(-4)};
  ${({ theme, altcolor }) => {
    if (theme.palette.mode !== 'dark') {
      return `background: #fff;`;
    }

    if (altcolor) {
      return `background: ${theme.palette.background.paper};`;
    }

    // TODO: Replace with theme colors & enable overrides
    const gradient1 = 'rgba(92, 104, 196, 0.4)';
    const gradient2 = 'rgba(38, 56, 196, 0.36)';
    const gradient3 = 'rgba(78, 92, 196, 0.4)';
    return `background: linear-gradient(216.93deg, ${gradient1} 17.84%, ${gradient2} 34.53%, ${gradient3} 78.54%);`;
  }}
  padding: 0 ${TIMELINE_SIDES_PADDING}px;

  ${({ theme }) => {
    const SCROLL_TRACK_COLOR = theme.palette.mode === 'dark' ? '#252A46' : '#f0f2f5';
    const SCROLL_THUMB_COLOR = theme.palette.mode === 'dark' ? '#121420' : '#c1c5c9';

    return `
      &::-webkit-scrollbar {
        height: 30px;
      }
    
      &::-webkit-scrollbar-track {
        background: ${SCROLL_TRACK_COLOR};
        border-radius: 0;
      }
    
      &::-webkit-scrollbar-thumb {
        background: ${SCROLL_THUMB_COLOR};
        border-radius: 15px;
        border: 11px solid ${SCROLL_TRACK_COLOR};
        min-width: 35px; // without min-width set to some bigger value scrollbar thumb will simply disappear when get too small (because its border has the same color as track color)
      }
    
      @supports not selector(::-webkit-scrollbar) {
        & {
          scrollbar-color: ${SCROLL_THUMB_COLOR} ${SCROLL_TRACK_COLOR};
        }
      }
    `;
  }}
`;

const InnerWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const AddLayerButton = styled(Button)`
  position: sticky;
  left: 0;
  color: ${({ theme }) => theme.palette.tertiary.contrastText};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const LayersDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  width: 100%;
`;

const FrozenTrayTitle = styled(Typography)`
  position: sticky;
  left: ${({ theme }) => theme.spacing(1)};
  display: inline-block;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 1.46rem;
  line-height: 1.85rem;
`;

const Divider = styled.div`
  position: absolute;
  height: 1px;
`;

const TraysDivider = styled(Divider)`
  left: -${TIMELINE_SIDES_PADDING}px;
  right: -${TIMELINE_SIDES_PADDING}px;
  min-width: 100vw;
  background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#1b1d27' : '#E8E8E9')};
`;

const ScrollDivider = styled(Divider)`
  left: 0;
  right: 0;
  background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#7e8bf2' : 'transparent')};
`;
